import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, IconButton, Chip,
  Accordion, AccordionSummary, AccordionDetails, Divider,
  CircularProgress, Alert
} from '@mui/material';
import { ArrowBack, ExpandMore, Mic, GraphicEq, AutoAwesome, Delete } from '@mui/icons-material';
import { api } from '../api';

const STATUS_COLORS = {
  pending: { color: '#B54708', bg: '#FFFAEB', border: '#FEC84B', label: 'Pending transcription' },
  done: { color: '#027A48', bg: '#ECFDF3', border: '#6CE9A6', label: 'Transcribed' },
  error: { color: '#B42318', bg: '#FEF3F2', border: '#FDA29B', label: 'Error' },
};

export default function ClassRecordingsPage() {
  const { classId } = useParams();
  const navigate = useNavigate();
  const [recordings, setRecordings] = useState([]);
  const [className, setClassName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [cls, recs] = await Promise.all([
          api.getClass(classId),
          api.getRecordingsByClass(classId),
        ]);
        setClassName(cls.name || '');
        setRecordings(recs);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [classId]);

  const handleDelete = async (id) => {
    try {
      await api.deleteRecording(id);
      setRecordings(r => r.filter(rec => rec.id !== id));
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: 700, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
        <IconButton onClick={() => navigate('/classes')}><ArrowBack /></IconButton>
        <Box>
          <Typography variant="h5" fontWeight={700}>Recordings</Typography>
          {className && (
            <Typography variant="body2" color="text.secondary">{className}</Typography>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {recordings.length === 0 ? (
        <Box sx={{
          textAlign: 'center', py: 6, px: 3,
          bgcolor: '#F9FAFB', borderRadius: 3, border: '1px dashed #D0D5DD'
        }}>
          <Mic sx={{ fontSize: 40, color: '#98A2B3', mb: 1 }} />
          <Typography fontWeight={600} color="#344054">No recordings yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Upload a recording from the Attendance page after marking a session
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {recordings.map(rec => {
            const status = STATUS_COLORS[rec.processingStatus] || STATUS_COLORS.pending;
            return (
              <Card key={rec.id} sx={{ border: '1px solid #EAECF0', boxShadow: 'none' }}>
                <CardContent sx={{ pb: '12px !important' }}>
                  {/* Header row */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <GraphicEq sx={{ color: '#6366f1', fontSize: 20 }} />
                      <Box>
                        <Typography fontWeight={600} fontSize="0.95rem">
                          {rec.sessionDate || 'Unknown date'}
                        </Typography>
                        <Chip
                          label={status.label}
                          size="small"
                          sx={{
                            mt: 0.5,
                            fontSize: '0.7rem',
                            color: status.color,
                            bgcolor: status.bg,
                            border: `1px solid ${status.border}`,
                          }}
                        />
                      </Box>
                    </Box>
                    <IconButton size="small" color="error" onClick={() => handleDelete(rec.id)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>

                  {/* Audio player */}
                  {rec.audioUrl && (
                    <Box sx={{ mt: 1.5 }}>
                      <audio
                        controls
                        src={rec.audioUrl}
                        style={{ width: '100%', height: 36 }}
                      />
                    </Box>
                  )}

                  {/* Summary */}
                  {rec.summary && (
                    <Box sx={{
                      mt: 1.5, p: 1.5, borderRadius: 2,
                      bgcolor: '#F8F9FF', border: '1px solid #E0E4FF'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
                        <AutoAwesome sx={{ fontSize: 14, color: '#6366f1' }} />
                        <Typography variant="caption" fontWeight={700} color="#4f46e5">
                          AI Summary
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="#344054" sx={{ lineHeight: 1.6 }}>
                        {rec.summary}
                      </Typography>
                    </Box>
                  )}

                  {/* Full transcript (collapsed) */}
                  {rec.transcript && (
                    <Accordion
                      disableGutters
                      elevation={0}
                      sx={{
                        mt: 1, border: '1px solid #EAECF0', borderRadius: '8px !important',
                        '&:before': { display: 'none' },
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: 36, py: 0 }}>
                        <Typography variant="caption" fontWeight={600} color="text.secondary">
                          Full Transcript
                        </Typography>
                      </AccordionSummary>
                      <Divider />
                      <AccordionDetails sx={{ pt: 1.5 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                          {rec.transcript}
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {rec.processingStatus === 'pending' && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Transcription runs Sunday night. Check back Monday.
                    </Typography>
                  )}
                  {rec.processingStatus === 'error' && rec.errorMessage && (
                    <Alert severity="error" sx={{ mt: 1, py: 0 }}>{rec.errorMessage}</Alert>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
